import {cn} from '../lib/utils'
import {Icon} from './Icon'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import {
  type AriaAttributes,
  createContext,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

const VIEWPORT_GUTTER_PX = 16
/** Matches the panel's `p-16`. */
const PANEL_PADDING_PX = 16

/** `transient`: opened by hover or focus, closes when they end. `pinned`: opened by a click or tap. */
type OpenState = 'closed' | 'transient' | 'pinned'

interface TooltipContextValue {
  close: () => void
  togglePinned: () => void
  triggerRef: {current: HTMLButtonElement | null}
}

const TooltipContext = createContext<TooltipContextValue | null>(null)

function useTooltipContext(part: string): TooltipContextValue {
  const context = useContext(TooltipContext)
  if (!context) throw new Error(`<${part}> must be used within <Tooltip>`)
  return context
}

export interface TooltipProps {
  /** A `TooltipTrigger` and a `TooltipContent`. */
  children: ReactNode
}

/**
 * Tooltip
 *
 * A small panel above a trigger, with an arrow pointing at it. Built on `@radix-ui/react-tooltip`,
 * which handles positioning, viewport collision and hover/focus/Escape/outside-press. Compose it
 * from `TooltipTrigger` and `TooltipContent`; the content is filled with `TooltipTitle`,
 * `TooltipDescription`, `TooltipLinks` and `TooltipClose`, each optional and laid out by
 * `TooltipContent` (text column on the left, close button top-right).
 *
 * **Opening:**
 * - Mouse and keyboard: opens on hover / focus and closes when they end.
 * - Click or tap: toggles it and pins it open until the next click, Escape, a press outside, or
 *   `TooltipClose`. Radix tooltips ignore touch by design, so this is what makes them usable on
 *   mobile.
 *
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=17303-11764&m=dev
 *
 * @example
 * <Tooltip>
 *   <TooltipTrigger asChild>
 *     <button type="button" aria-label="Score information">
 *       <Icon name="info" size="small" />
 *     </button>
 *   </TooltipTrigger>
 *   <TooltipContent align="start">
 *     <TooltipTitle>Perfect fit</TooltipTitle>
 *     <TooltipDescription>Used for your last purchase.</TooltipDescription>
 *     <TooltipClose aria-label="Close" />
 *   </TooltipContent>
 * </Tooltip>
 */
export function Tooltip({children}: TooltipProps) {
  const [openState, setOpenState] = useState<OpenState>('closed')
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  /* Radix reports hover/focus starting and ending here; a pinned tooltip ignores the ending. */
  const handleOpenChange = (isOpen: boolean) => {
    setOpenState((current) => {
      if (isOpen) return current === 'closed' ? 'transient' : current
      return current === 'pinned' ? current : 'closed'
    })
  }

  const context = useMemo<TooltipContextValue>(
    () => ({
      close: () => setOpenState('closed'),
      togglePinned: () => setOpenState((current) => (current === 'pinned' ? 'closed' : 'pinned')),
      triggerRef,
    }),
    [],
  )

  return (
    <TooltipContext.Provider value={context}>
      <TooltipPrimitive.Provider delayDuration={0}>
        <TooltipPrimitive.Root open={openState !== 'closed'} onOpenChange={handleOpenChange}>
          {children}
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </TooltipContext.Provider>
  )
}

export type TooltipTriggerProps = TooltipPrimitive.TooltipTriggerProps

/**
 * The element that opens the tooltip. Renders a `button`; pass `asChild` to use your own single
 * focusable child instead (it receives the ref, handlers and ARIA attributes). A handler you pass
 * that calls `preventDefault()` opts that event out of the click-to-pin behaviour.
 */
export function TooltipTrigger({onClick, onPointerDown, ...props}: TooltipTriggerProps) {
  const {togglePinned, triggerRef} = useTooltipContext('TooltipTrigger')

  /* `preventDefault` skips Radix's own handlers, which close the tooltip on press and click. */
  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event)
    event.preventDefault()
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    event.preventDefault()
    togglePinned()
  }

  return (
    <TooltipPrimitive.Trigger
      {...props}
      ref={triggerRef}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    />
  )
}

export interface TooltipContentProps {
  /** Any of `TooltipTitle`, `TooltipDescription`, `TooltipLinks`, `TooltipClose`. */
  children: ReactNode
  /** Alignment against the trigger; the panel sits above it and flips when there is no room. */
  align?: TooltipPrimitive.TooltipContentProps['align']
  /**
   * Shift along the alignment axis, in px. For `start`/`end` it defaults to overhanging the
   * trigger by the panel padding, so the text lines up with the trigger's edge and the arrow keeps
   * clear of the rounded corner.
   */
  alignOffset?: number
  /**
   * Render into `document.body` (default). Pass `false` when the host scopes its CSS to a subtree,
   * so the panel stays inside it and its classes keep matching.
   */
  portal?: boolean
  className?: string
}

/**
 * The panel. A two-column grid: the text parts stack in the first column, `TooltipClose` takes
 * the top of the second; the second column collapses to nothing when there is no close button.
 */
export function TooltipContent({
  children,
  align = 'center',
  alignOffset = align === 'center' ? 0 : -PANEL_PADDING_PX,
  portal = true,
  className,
}: TooltipContentProps) {
  const {close, triggerRef} = useTooltipContext('TooltipContent')

  const panel = (
    <TooltipPrimitive.Content
      align={align}
      alignOffset={alignOffset}
      className={cn(
        'z-50 grid w-[309px] max-w-[calc(100vw-32px)] grid-cols-[minmax(0,1fr)_auto] items-start gap-y-8 rounded-lg bg-surface p-16 text-left text-xs leading-xs text-text drop-shadow-tooltip',
        className,
      )}
      collisionPadding={VIEWPORT_GUTTER_PX}
      side="top"
      sideOffset={8}
      onEscapeKeyDown={close}
      onPointerDownOutside={(event) => {
        if (triggerRef.current?.contains(event.target as Node)) {
          event.preventDefault()
          return
        }
        close()
      }}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-surface" height={6} width={17} />
    </TooltipPrimitive.Content>
  )

  return portal ? <TooltipPrimitive.Portal>{panel}</TooltipPrimitive.Portal> : panel
}

interface TooltipTextProps {
  children: ReactNode
  className?: string
}

/** Optional bold line above the description. */
export function TooltipTitle({children, className}: TooltipTextProps) {
  return <span className={cn('col-start-1 font-medium', className)}>{children}</span>
}

/** Body text. */
export function TooltipDescription({children, className}: TooltipTextProps) {
  return <span className={cn('col-start-1 font-light', className)}>{children}</span>
}

/** A row under the description, e.g. one or two `<a>` elements (underlined automatically). */
export function TooltipLinks({children, className}: TooltipTextProps) {
  return (
    <span
      className={cn('col-start-1 mt-8 flex flex-wrap gap-16 font-light [&_a]:underline', className)}
    >
      {children}
    </span>
  )
}

export type TooltipCloseProps = Required<Pick<AriaAttributes, 'aria-label'>>

/**
 * Icon-only close button in the top-right corner; its `aria-label` is required for that reason.
 * Pulled 6px into the panel padding, so it sits 10px from the edges while the text keeps 16px.
 */
export function TooltipClose({'aria-label': ariaLabel}: TooltipCloseProps) {
  const {close} = useTooltipContext('TooltipClose')

  return (
    <button
      aria-label={ariaLabel}
      className="col-start-2 row-start-1 ms-16 -me-6 -mt-6 flex text-text"
      type="button"
      onClick={close}
    >
      <Icon name="close" size="small" />
    </button>
  )
}
